import { useQuery } from '@tanstack/react-query';
import type { ColumnDef, DatasetQueryConfig } from '@deckspace/types';
import type { WorkspaceId } from '@deckspace/ids';
import { useApi } from '../lib/api-context';
import { queryKeys } from '../lib/queryKeys';

interface UseDatasetRowsResult {
  data: Record<string, unknown>[] | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

interface UseDatasetSchemaResult {
  data: ColumnDef[] | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

interface UseDatasetResult {
  data: { rows: Record<string, unknown>[]; columnDefs: ColumnDef[] } | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Fetch a dataset's rows. Keyed on `(workspaceId, datasetName, queryConfig)`
 * so different queryConfigs cache independently. SSE broadcasts invalidate
 * the `['datasets', workspaceId]` prefix to refetch all rows queries for
 * the affected workspace (see `useWorkspaceEvents`).
 */
export function useDatasetRows(
  workspaceId: string | undefined,
  datasetName: string | undefined,
  queryConfig?: DatasetQueryConfig
): UseDatasetRowsResult {
  const api = useApi();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.datasets.rows(workspaceId ?? '', datasetName ?? '', queryConfig),
    queryFn: () => {
      if (!workspaceId || !datasetName) {
        throw new Error('useDatasetRows requires workspaceId and datasetName');
      }
      return api.queryDataset(workspaceId as WorkspaceId, datasetName, queryConfig);
    },
    enabled: Boolean(workspaceId && datasetName),
  });

  return {
    data,
    isLoading,
    error,
    refetch: () => {
      void refetch();
    },
  };
}

/**
 * Fetch a dataset's column schema. Keyed on `(workspaceId, datasetName)` —
 * `queryConfig` deliberately omitted so changing filters/orderBy/limit does
 * NOT re-fetch the schema. SSE broadcasts invalidate the `['datasets', workspaceId]`
 * prefix (covers schema changes via `datasets.workspace`).
 */
export function useDatasetSchema(
  workspaceId: string | undefined,
  datasetName: string | undefined
): UseDatasetSchemaResult {
  const api = useApi();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.datasets.schema(workspaceId ?? '', datasetName ?? ''),
    queryFn: async () => {
      if (!workspaceId || !datasetName) {
        throw new Error('useDatasetSchema requires workspaceId and datasetName');
      }
      const dataset = await api.getDataset(workspaceId as WorkspaceId, datasetName);
      return dataset.columns;
    },
    enabled: Boolean(workspaceId && datasetName),
  });

  return {
    data,
    isLoading,
    error,
    refetch: () => {
      void refetch();
    },
  };
}

/**
 * Composing wrapper for callers that need both rows and columnDefs in one
 * result object. Internally uses the two split hooks — schema fetches once
 * regardless of queryConfig changes.
 */
export function useDataset(
  workspaceId: string | undefined,
  datasetName: string | undefined,
  queryConfig?: DatasetQueryConfig
): UseDatasetResult {
  const rowsQuery = useDatasetRows(workspaceId, datasetName, queryConfig);
  const schemaQuery = useDatasetSchema(workspaceId, datasetName);

  const isLoading = rowsQuery.isLoading || schemaQuery.isLoading;
  const error = rowsQuery.error ?? schemaQuery.error;
  const data =
    rowsQuery.data !== undefined && schemaQuery.data !== undefined
      ? { rows: rowsQuery.data, columnDefs: schemaQuery.data }
      : undefined;

  return {
    data,
    isLoading,
    error,
    refetch: () => {
      rowsQuery.refetch();
      schemaQuery.refetch();
    },
  };
}
